import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Contratto } from '@/lib/graphql/types'

export type AuthUser = {
  username: string
  email?: string
  phone?: string
  name?: string
  givenName?: string
  familyName?: string
  codiceCliente?: string
}

type AuthState = {
  status: 'unknown' | 'authenticated' | 'unauthenticated'
  user: AuthUser | null
  /** Codice contratto attualmente selezionato dall'utente (se ha più di un POD). */
  activeContractCode: string | null
  setUser: (user: AuthUser | null) => void
  setStatus: (status: AuthState['status']) => void
  setActiveContract: (code: string | null) => void
  reset: () => void
}

/**
 * Persistiamo solo `activeContractCode` (preference utente cross-session).
 * `user` e `status` sono ricostruiti al boot da Amplify (`loadCurrentUser`).
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'unknown',
      user: null,
      activeContractCode: null,
      setUser: (user) =>
        set({ user, status: user ? 'authenticated' : 'unauthenticated' }),
      setStatus: (status) => set({ status }),
      setActiveContract: (code) => set({ activeContractCode: code }),
      reset: () =>
        set({ user: null, status: 'unauthenticated', activeContractCode: null }),
    }),
    {
      name: 'enermed-auth',
      partialize: (s) => ({ activeContractCode: s.activeContractCode }),
    },
  ),
)

/**
 * Helper: ritorna il contratto attivo dato l'elenco contratti propri + monitorati.
 *
 * Logica replicata dal mobile (Route.js): `contratti` e `monitoraggio` sono
 * trattati insieme come "tutti i POD che l'utente può consultare".
 *  - Se in totale c'è 1 solo contratto, lo restituisce automaticamente
 *  - Se l'utente ha scelto un `activeContractCode`, lo cerca in entrambe le
 *    liste e lo restituisce se trovato
 *  - Altrimenti fallback al primo contratto proprio (o al primo monitorato
 *    se non ha contratti propri)
 */
export function pickActiveContract(
  contratti: Contratto[] | undefined,
  monitoraggio: Contratto[] | undefined,
  activeCode: string | null,
): Contratto | null {
  const own = contratti ?? []
  const monitored = monitoraggio ?? []
  const all = [...own, ...monitored]
  if (all.length === 0) return null
  if (all.length === 1) return all[0]
  if (activeCode) {
    const match = all.find((c) => c.codice_contratto === activeCode)
    if (match) return match
  }
  return all[0]
}
